import type { PayabliRail } from '@/lib/payments/payabli';
import {
  computePaymentMethodDiscount,
  effectivePaymentMethodDiscountPercent,
  formatDiscountPercent,
  surchargeToDiscountPercent,
  type PaymentMethodDiscountSettings,
} from './paymentMethodDiscount';

/**
 * Customer-facing direct ACH/check discount when enabled; otherwise preserves
 * the legacy card surcharge disclosure for existing tenants.
 *
 * Rail availability is passed explicitly so stale surcharge data cannot claim
 * savings against a card price that the customer cannot actually choose.
 */
export function cardServiceFeeNote(
  opts: PaymentMethodDiscountSettings & {
    /** Exact direct-discount cents for principal only; zero suppresses savings copy. */
    discountCents?: number | null;
    /** Formatted direct-discount amount, distinct from any legacy fee amount. */
    discountAmount?: string | null;
    surchargePercent: number;
    /** Preformatted savings amount (e.g. "$3.00"), when the exact figure is known. */
    feeAmount?: string | null;
    /** Exact ACH/check price in the caller's currency unit. */
    achOrCheckPrice?: number | null;
    /** Exact difference between the ACH/check and card prices in the same unit. */
    cardPriceDifference?: number | null;
    /** Whether a card price is actually available on this checkout. */
    cardOffered?: boolean;
    achOffered: boolean;
    /** Current Payabli rail; retained so every pay surface follows the same contract. */
    selectedRail?: PayabliRail;
    /**
     * Which funding rule the charge behind this surface follows. Required, so a
     * new pay surface cannot inherit either copy by omission.
     *
     * `true` — the fee is contingent on a card funding class this surface cannot
     * know yet. Payabli reports funding (`binData.binCardType`) only when it
     * tokenizes the card, which happens AFTER the payer commits, and the charge
     * applies the fee only when that comes back CREDIT — debit, prepaid and an
     * empty BIN lookup all pay the base. So a pre-tokenization surface must
     * present the difference as conditional: naming one "card price" asserts a
     * total the server will not charge roughly half of US card payers, and makes
     * the ACH savings pitch false for anyone who was going to use a debit card
     * anyway. Every Payabli customer pay surface (invoice, portal invoice,
     * payment link, deposit) is this case.
     *
     * `false` — the quoted card total really is charged. Stripe's hosted surfaces
     * fix the amount before the instrument exists, charge the fee, and refund it
     * afterwards if the settled card turns out not to be credit.
     */
    creditCardOnly: boolean;
  },
): string {
  if (opts.achCheckDiscountEnabled === true) {
    const discount = computePaymentMethodDiscount(opts, 0, 'ACH');
    if (!discount.enabled || !opts.achOffered || opts.discountCents === 0) return '';
    const savings = `${formatDiscountPercent(discount.percent)}${
      opts.discountAmount ? ` (${opts.discountAmount})` : ''
    }`;
    return `Pay by bank transfer and save ${savings} from the final balance, excluding tips. Credit card, debit card, and cash receive no discount.`;
  }
  const cardOffered = opts.cardOffered ?? true;
  if (!cardOffered) return '';

  const hasExactPrices =
    Number.isFinite(opts.achOrCheckPrice) &&
    Number.isFinite(opts.cardPriceDifference) &&
    Number(opts.achOrCheckPrice) >= 0 &&
    Number(opts.cardPriceDifference) >= 0;
  if (hasExactPrices && Number(opts.cardPriceDifference) === 0) {
    return '';
  }

  const discount = formatDiscountPercent(
    hasExactPrices
      ? effectivePaymentMethodDiscountPercent(
          Number(opts.achOrCheckPrice),
          Number(opts.cardPriceDifference),
          opts.surchargePercent,
        )
      : surchargeToDiscountPercent(opts.surchargePercent),
  );
  const savings = `${discount}${opts.feeAmount ? ` (${opts.feeAmount})` : ''}`;
  if (opts.creditCardOnly) {
    // Scope the difference to the instruments it can legally ride, and say
    // plainly that the others pay less. Surcharging debit is barred by the
    // network rules and illegal in several states, which is exactly why the
    // charge path suppresses the fee for them.
    return opts.achOffered
      ? `The ${savings} card price difference applies to credit cards only; debit cards, bank transfers and checks pay the lower price.`
      : `The ${savings} card price difference applies to credit cards only; debit cards pay the lower price. ACH/check is not available online.`;
  }
  if (opts.achOffered) {
    return `Pay by bank transfer and save ${savings} compared with the card price.`;
  }
  return opts.feeAmount
    ? `The card price includes a ${opts.feeAmount} difference from ACH/check pricing; ACH/check is not available online.`
    : 'This payment uses card pricing; ACH/check is not available online.';
}
